import { ErrorAlert, Spinner } from '@lumlab/platform-ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { authClient } from './auth.ts';
import { useAccountState } from './account.ts';

/** The server enforces security independently; this guard directs the user to the next required step. */
export function RequireAccount({
    children,
    securityRequired = true,
    onboardingRequired = true,
}: {
    children: ReactNode;
    securityRequired?: boolean;
    onboardingRequired?: boolean;
}) {
    const { t } = useTranslation();
    const session = authClient.useSession();
    const account = useAccountState(session.data?.user.id);
    if (session.isPending || (session.data && account.isPending))
        return (
            <div className="flex min-h-dvh items-center justify-center">
                <Spinner label={t('platform.shell.loading')} />
            </div>
        );
    if (!session.data) return <Navigate to="/sign-in" replace />;
    if (account.error) return <ErrorAlert error={account.error} />;
    if (securityRequired && account.data && (account.data.twoFactorRequired || account.data.twoFactorEnabled)) {
        if (!account.data.twoFactorEnabled) return <Navigate to="/security" replace />;
        if (!account.data.twoFactorVerified) return <Navigate to="/two-factor" replace />;
    }
    if (onboardingRequired && account.data?.onboardingRequired) return <Navigate to="/welcome" replace />;
    return children;
}
